import React, { useCallback, useState } from 'react'
import { useStyles } from '../EditPrivateOfferWrapper.styles'
import { Form, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik'
import { OffersWithErrors } from '../../../modules/privateOffer/privateOfferCreation/reducer'
import privateOfferAddEditValidationSchema from '../../../common/utils/privateOfferAddEditValidationSchema'
import {
  Button,
  ModalConfirm,
  ModalSizeVariants,
  StepperComponent,
} from '@labrav/react-components'
import { DetailedOffer } from '@labrav/marketplace'
import { useDispatch, useSelector } from '../../../store/hooks'
import { useParams } from 'react-router-dom'
import { stepsForPrivateOffer } from '../NewPrivateOfferWrapper'
import { Product } from '../../../modules/productsListing/reducer'
import { get, isEmpty } from 'lodash'
import { PrivateOfferAddEdit } from './PrivateOfferCreation/PrivateOfferAddEdit'
import { sendPrivateOffer } from '../../../modules/privateOffer/privateOfferCreation/action'
import { uploadFiles } from '../../../modules/uploadEulaFiles/action'
import { defaultNow } from '@labrav/common'
import {
  checkIsErrOnRecipientOrStakeHolder,
  checkIfFormikTouchedArrayHasErr,
  isSignleFieldsHaveErr,
} from '../../../common/utils/offerValidationHelper'
import {
  EulaType,
  OfferRecipients,
  OfferStakeholders,
} from '../../../modules/privateOffer/reducer'
import { bucketName } from '../../../common/utils/constants'
import { LoadingTypes, isLoading } from '@labrav/common'
export type PrivateOfferAddEditFormikProps = {
  product: Product
  externalBaseURL?: string
  crmOpportunityId?: string
  crmObjectLink?: string
}
export const PrivateOfferAddEditFormik: React.FC<
  PrivateOfferAddEditFormikProps
> = ({ product, externalBaseURL, crmOpportunityId, crmObjectLink }) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const { productId = '' } = useParams<{
    productId: string
  }>()
  const partnerId = useSelector<string>(
    state => state.AccountData.user.partnerData?.partnerId || ''
  )
  const privateOffer = useSelector<OffersWithErrors>(
    state =>
      state.privateOffersInCreation.currentPrivateOfferInCreation[productId] ||
      ({} as OffersWithErrors)
  )
  const [selectedEulaFiles, setSelectedEulaFiles] = useState<File[]>([])
  const [openModal, setOpenModal] = useState<boolean>(false)
  const noOps = () => void 0
  const [activeStep, _] = useState<number>(0)
  const handleOpenModal = useCallback(async () => {
    setOpenModal(true)
  }, [privateOffer, product, productId])

  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )

  const handleClose = useCallback(() => {
    setOpenModal(false)
  }, [openModal])

  const fileUploadCallback = async (
    uploadEulaFiles: string[],
    saveAsDraft: boolean,
    formikOffer: OffersWithErrors
  ) => {
    const updatedFormikOffer = { ...formikOffer }
    updatedFormikOffer.eulaFile = (updatedFormikOffer.eulaFile || []).concat(
      uploadEulaFiles
    )
    await dispatch(
      sendPrivateOffer({
        partnerId,
        productId,
        saveAsDraft,
        crmOpportunityId,
        crmObjectLink,
        formikOffer: updatedFormikOffer,
      })
    )
    setSelectedEulaFiles([])
  }

  const uploadToS3 = useCallback(
    async (saveAsDraft: boolean, formikOffer: OffersWithErrors) => {
      await dispatch(
        uploadFiles({
          partnerId,
          productId,
          filesToBeUploaded: selectedEulaFiles,
          assetType: 'eula_files',
          folder: 'private_offer_eula',
          defaultNow,
          saveAsDraft,
          crmOpportunityId,
          crmObjectLink,
          fileUploadCallback: (uploadEulaFiles: string[]) =>
            fileUploadCallback(uploadEulaFiles, saveAsDraft, formikOffer),
          bucketName,
          showRealFileName: true,
        })
      )
    },
    [privateOffer, productId, partnerId, selectedEulaFiles]
  )

  const handleSentToBuyer = useCallback(
    (saveAsDraft: boolean, formikOffer: OffersWithErrors) => async () => {
      if (saveAsDraft) {
        setOpenModal(false)
      }
      if (!isEmpty(selectedEulaFiles)) {
        await uploadToS3(saveAsDraft, formikOffer)
      } else {
        await dispatch(
          sendPrivateOffer({
            partnerId,
            productId,
            saveAsDraft,
            crmOpportunityId,
            crmObjectLink,
            formikOffer,
          })
        )
      }
    },
    [privateOffer, product, productId, openModal, selectedEulaFiles]
  )

  const isOfferSaveAsDraftDisabled = (
    formikProps: FormikProps<OffersWithErrors>
  ) => {
    const { errors, touched } = formikProps
    const singleField = [
      'companyName',
      'buyerCloudBillingId',
      'emailCustomMessage',
      'privateOfferName',
      'offerExpirationDate',
      'plan.serviceLength',
      'plan.paymentType',
      'plan.flatFeePrice',
      'plan.tcv',
      'plan.discount',
    ]
    const singleFieldTouchedErr = isSignleFieldsHaveErr(
      singleField,
      errors,
      touched
    )

    const recipentErr =
      touched?.offerRecipients &&
      errors?.offerRecipients &&
      touched?.offerRecipients?.some((recipientTouched, idx) =>
        checkIsErrOnRecipientOrStakeHolder(
          recipientTouched,
          idx,
          errors?.offerRecipients as unknown as FormikErrors<OfferRecipients>[]
        )
      )
    const stakeholderErr =
      touched?.offerStakeholders &&
      errors?.offerStakeholders &&
      (
        touched?.offerStakeholders as unknown as FormikTouched<OfferStakeholders>[]
      ).some((offerStakehodersTouched, idx) =>
        checkIsErrOnRecipientOrStakeHolder(
          offerStakehodersTouched,
          idx,
          errors?.offerStakeholders as unknown as FormikErrors<OfferStakeholders>[]
        )
      )
    const metricErr =
      checkIfFormikTouchedArrayHasErr(
        formikProps as unknown as FormikProps<Record<string, unknown>>,
        'plan.metricPricing'
      ) &&
      get(touched?.plan, 'metricPricing', []).some(
        (meteringPricingItemTouched, idx) =>
          get(meteringPricingItemTouched, 'discount') &&
          !isEmpty(get(errors?.plan, `metricPricing.[${idx}].discount`))
      )

    const installmentsErr =
      checkIfFormikTouchedArrayHasErr(
        formikProps as unknown as FormikProps<Record<string, unknown>>,
        'plan.installmentInfo'
      ) &&
      get(touched?.plan, 'installmentInfo', []).some(
        (installmentItemTouched, idx) =>
          get(installmentItemTouched, 'metricPricing', []).length > 0 &&
          get(errors?.plan, `installmentInfo[${idx}].metricPricing`, [])
            .length > 0 &&
          get(installmentItemTouched, 'metricPricing', []).some(
            (installmentMetricItemTouched, metricIndex) =>
              get(installmentMetricItemTouched, 'discount') &&
              !isEmpty(
                get(
                  errors?.plan,
                  `installmentInfo[${idx}].metricPricing[${metricIndex}].discount`
                )
              )
          )
      )
    return (
      singleFieldTouchedErr ||
      recipentErr ||
      stakeholderErr ||
      metricErr ||
      installmentsErr
    )
  }
  return (
    <Formik
      initialValues={privateOffer}
      validationSchema={privateOfferAddEditValidationSchema}
      validateOnMount={true}
      onSubmit={noOps}
      enableReinitialize={true}
    >
      {formikProps => {
        const eulaCheck =
          (formikProps.values.eulaType === EulaType.CUSTOM &&
            formikProps.values.eulaFile?.length === 0 &&
            selectedEulaFiles.length === 0) ||
          !isEmpty(formikProps.errors?.eulaFile)
        return (
          <Form>
            <div className={classes.root}>
              <div className={classes.stepperWrapper} data-testid="stepperDiv">
                <StepperComponent
                  activeStep={activeStep}
                  steps={stepsForPrivateOffer}
                />
                <PrivateOfferAddEdit
                  product={product}
                  externalBaseURL={externalBaseURL}
                  selectedEulaFiles={selectedEulaFiles}
                  setSelectedEulaFiles={setSelectedEulaFiles}
                />
                <div className={classes.buttonContainer}>
                  <Button
                    styleType="secondary"
                    onClick={handleOpenModal}
                    disabled={
                      loading || isOfferSaveAsDraftDisabled(formikProps)
                    }
                    className={classes.saveButton}
                    data-testid={'save-as-draft'}
                  >
                    {'Save and preview'}
                  </Button>
                  <Button
                    styleType="primary"
                    onClick={handleSentToBuyer(false, formikProps?.values)}
                    disabled={
                      loading || !isEmpty(formikProps.errors) || eulaCheck
                    } //Need to Add one validator function which check all the required fields are not empty
                    data-testid={'send-to-buyer'}
                  >
                    {'Send to buyer'}
                  </Button>
                </div>
              </div>
              <ModalConfirm
                title={'Preview'}
                subTitle={''}
                content={
                  <DetailedOffer
                    offer={
                      {
                        ...formikProps.values,
                        offerName: formikProps.values.privateOfferName,
                        stakeholders: formikProps.values.offerStakeholders,
                        labraPlanFeatures: isEmpty(
                          formikProps.values.labraPlanFeatures
                        )
                          ? product?.plans?.find(
                              plan =>
                                plan.planId ===
                                formikProps?.values?.plan?.planId
                            )?.features
                          : formikProps.values.labraPlanFeatures,
                      } as unknown as DetailedOffer
                    }
                    planType={product.productType}
                  />
                }
                open={openModal}
                titleAcceptButton={'Okay'}
                onAccept={handleSentToBuyer(true, formikProps?.values)}
                onDecline={handleClose}
                variant={ModalSizeVariants.medium}
                hideCancelButton={true}
              />
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}
